import React, { useEffect, useState } from "react";
import Axios from "axios";
import MACSearch from "../CustomComponents/MACSearch/MACSearch";
import CustomSelect from "../Body/CustomChartComponents/CustomSelect/CustomSelect";
import CustomIconButton from "../Body/CustomChartComponents/CustomIconButton/CustomIconButton";
import { ArrowDown } from "../CustomComponents/SwiftIcon/Icons";
import { Alert } from "../Body/CustomChartComponents/CustomAlert/CustomAlert";
import Pulse from "../Loader/Pulse";
import { COLOR_VARS } from "../../exports/ColorVars";
import { getDeviceSize } from "../../exports/InteractiveUtils";

const REQUEST_BASE_URL = process.env.REACT_APP_REQUEST_BASE_URL;

const report_types = ["Factsheet Statement", "Gain & Loss Statement"];
const report_targets = ["Individual Customer", "Family Portfolio"];

// Which book the statement covers. `requires` are the activation verticals
// (swiftfolios_vertical_activation: "equity", "mutual_funds") the customer —
// or, for a family, at least one of its members — must hold to be offered it.
const VERTICAL_OPTIONS = [
  { label: "Stocks", api: "stocks", requires: ["equity"] },
  { label: "Mutual Fund", api: "mutual_funds", requires: ["mutual_funds"] },
  { label: "Combined", api: "combined", requires: ["equity", "mutual_funds"] },
];
const verticalToApi = (label) =>
  VERTICAL_OPTIONS.find((o) => o.label === label)?.api || "stocks";

function ReportGenerate({ user_role_type, customers, portfolios }) {
  const [loading, setLoading] = useState(false);
  const [accountCode, setAccountCode] = useState(false);
  const [portfolioID, setPortfolioID] = useState(false);
  const [reportTarget, setReportTarget] = useState(report_targets[0]);
  const [reportType, setReportType] = useState(report_types[0]);
  const [reportYear, setReportYear] = useState(false);
  const [reportYears, setReportYears] = useState([]);
  const [reportVertical, setReportVertical] = useState("");
  const [verticalsMap, setVerticalsMap] = useState({});

  const isFamily = reportTarget === report_targets[1];

  useEffect(() => {
    Axios.get(`${REQUEST_BASE_URL}/reports/customer-verticals`)
      .then((response) => {
        const data = response.data;
        if (data) setVerticalsMap(data.verticals || {});
      })
      .catch(() => {});
  }, []);

  // Individual: only the books this customer has activated.
  // Family: the union across its members, since the statement simply omits a
  // book nobody in the family holds.
  const availableVerticals = React.useMemo(() => {
    const active = new Set();

    if (isFamily) {
      const family = portfolios.find(
        (p) => p.family_portfolio_id === portfolioID
      );
      (family?.family_portfolio_customers || []).forEach((m) =>
        (verticalsMap[m.customer_account_code] || []).forEach((v) =>
          active.add(v)
        )
      );
    } else {
      (verticalsMap[accountCode] || []).forEach((v) => active.add(v));
    }

    return VERTICAL_OPTIONS.filter((o) =>
      o.requires.every((v) => active.has(v))
    ).map((o) => o.label);
  }, [verticalsMap, accountCode, isFamily, portfolioID, portfolios]);

  // Keep the selection valid as the customer/family changes.
  useEffect(() => {
    if (availableVerticals.length === 0) {
      setReportVertical("");
      return;
    }
    if (!availableVerticals.includes(reportVertical)) {
      setReportVertical(availableVerticals[0]);
    }
  }, [availableVerticals, reportVertical]);

  useEffect(() => {
    if (user_role_type === "CUSTOMER" && reportTarget === report_targets[0]) {
      if (customers.length === 0) return;
      setCustomer(customers[0]);
    }
  }, [accountCode, customers, reportTarget]);

  function setCustomer(customer) {
    const account_code = customer.account_code;
    setAccountCode(account_code);
    getFYears("customer", false, account_code);
  }

  function setPortfolio(portfolio) {
    const portfolio_id = portfolio.family_portfolio_id;
    setPortfolioID(portfolio_id);
    getFYears("family", portfolio_id, false);
  }

  function getFYears(
    report_target,
    portfolio_id = false,
    account_code = false,
  ) {
    Axios.get(
      `${REQUEST_BASE_URL}/clientinfo/fyears?client_type=${report_target}&portfolio_id=${portfolio_id}&account_code=${account_code}`,
    )
      .then((response) => {
        const data = response.data;
        console.log(data);
        if (!data.error) {
          setReportYears(data.years);
          setReportYear(data.years[data.years.length - 1]);
        } else {
          setReportYears([]);
          setReportYear(false);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }

  function generateReport() {
    const report_target =
      reportTarget == "Individual Customer" ? "customer" : "family";
    const category = verticalToApi(reportVertical);
    setLoading(true);
    if (reportType === "Factsheet Statement") {
      Axios.get(
        `${REQUEST_BASE_URL}/reports/factsheet?report_type=${report_target}&portfolio_id=${portfolioID}&account_code=${accountCode}&category=${category}`,
      )
        .then((response) => {
          const data = response.data;
          if (!data.error) {
            setLoading(false);
            const report_url = data.report_url;
            DownloadReport(report_url);
          } else {
            setLoading(false);
            Alert({
              TitleText: "Report Download",
              Message: "Something went wrong while downloading the report ",
              BandColor: "var(--red)",
              AutoClose: {
                Active: true,
                Line: true,
                LineColor: "var(--red)",
                Time: 3,
              },
            });
          }
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      Axios.get(
        `${REQUEST_BASE_URL}/reports/gainloss?report_type=${report_target}&portfolio_id=${portfolioID}&account_code=${accountCode}&fy=${reportYear}&category=${category}`,
      )
        .then((response) => {
          const data = response.data;
          if (!data.error) {
            setLoading(false);
            const report_url = data.report_url;
            DownloadReport(report_url);
          } else {
            setLoading(false);
            Alert({
              TitleText: "Report Download",
              Message: "Something went wrong while downloading the report ",
              BandColor: "var(--red)",
              AutoClose: {
                Active: true,
                Line: true,
                LineColor: "var(--red)",
                Time: 3,
              },
            });
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }

  function DownloadReport(report_url) {
    const element = document.createElement("a");
    element.target = "_blank";
    element.href = report_url;
    element.download = "true";
    document.body.appendChild(element);
    element.click();

    setAccountCode(false);
    setReportYears([]);
    setPortfolioID(false);
    setReportType(report_types[0]);
    setReportYear(false);
    setLoading(false);
    setReportTarget(report_targets[0]);
  }

  function setTarget(report_target) {
    setAccountCode(false);
    setPortfolioID(false);
    setReportType(report_types[0]);
    setReportYear(false);
    setReportYears([]);
    setReportTarget(report_target);
  }

  const size = getDeviceSize()

  return (
    <>
      <div className="report-generate-header">
        <p className="report-generate-title">Generate Report</p>
      </div>
      <div className="report-generate-content">
        {loading && (
          <>
            <div className="report-generate-loading">
              <Pulse />
              {/* <p>Generating report, please wait...</p> */}
            </div>
          </>
        )}

        <div className="report-generate-wrapper">
          <div className="report-section-target">
            <CustomSelect
              heading="Report Type"
              options={report_targets}
              defaultIndex={0}
              value={reportTarget}
              onTypeChange={(value) => {
                setTarget(value);
              }}
            />
          </div>

          {reportTarget === "Individual Customer" ? (
            <>
              {user_role_type === "BROKER" ? (
                <>
                  <div className="report-section-customer">
                    <MACSearch
                      title="Search Customer"
                      showSearch={accountCode}
                      customers={customers}
                      onSelect={(customer) => {
                        setCustomer(customer);
                      }}
                    />
                  </div>
                </>
              ) : (
                <></>
              )}
            </>
          ) : (
            <>
              <MACSearch
                title="Search Family Portfolio"
                placeholder="Enter Portfolio Name"
                showSearch={portfolioID}
                customers={portfolios}
                onSelect={(portfolio) => {
                  setPortfolio(portfolio);
                }}
              />
            </>
          )}
          {(accountCode || portfolioID) && (
            <>
              <div className="report-generate-options">
                <div className="report-section-type">
                  <CustomSelect
                    heading="Type of Statement"
                    options={report_types}
                    defaultIndex={0}
                    value={reportType}
                    onTypeChange={(value) => {
                      setReportType(value);
                    }}
                  />
                </div>

                {availableVerticals.length > 0 ? (
                  <div className="report-section-type">
                    <CustomSelect
                      heading="Vertical"
                      options={availableVerticals}
                      defaultIndex={0}
                      value={reportVertical}
                      onTypeChange={(value) => {
                        setReportVertical(value);
                      }}
                    />
                  </div>
                ) : (
                  <p style={{ fontSize: 12, color: "var(--red)" }}>
                    {isFamily
                      ? "No member of this family has an active vertical."
                      : "This customer has no active vertical, so no statement can be generated."}
                  </p>
                )}

                {reportType === "Gain & Loss Statement" && (
                  <>
                    <div className="report-generate-year">
                      <CustomSelect
                        heading="Financial Year"
                        options={reportYears}
                        defaultIndex={reportYears.length - 1}
                        value={reportYear}
                        onTypeChange={(value) => {
                          setReportYear(value);
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <div className="report-section-button">
          <CustomIconButton
            className={size === "S" ? "btn-xl" : ""}
            disabled={(!accountCode && !portfolioID) || !reportVertical}
            onClick={generateReport}
            title="Download"
          />
        </div>
      </div>
    </>
  );
}

export default ReportGenerate;
